import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageForm from "components/pageForm";
import Button from "components/button";
import StyledInput from "components/styledInput/StyledInput";
import SubNavbar from "components/SubNavbar/SubNavbar";
import EmptyStateCompany from "components/emptyState";
import TemplateCard from "pages/missionTemplates/components/TemplateCard";
import TemplateCardSkeleton from "pages/missionTemplates/components/TemplateCardSkeleton";
import UseContext from "hooks/useContext";
import useFetchData from "hooks/useFetchData";
import ApiDataBase from "server";
import { Plus, SearchIcon } from "assets/icons";
import { filterMissionTemplates } from "pages/missionTemplates/utils/templateListing";

const SKELETON_CARDS = [1, 2, 3, 4, 5, 6];

const MissionTemplates = () => {
    useFetchData();
    const navigate = useNavigate();
    const {
        missionTemplates,
        setMissionTemplates,
        refreshMissionTemplates,
        setPreviewMissionTemplate,
        setModalType,
        setModalShow,
        resetTemplateData,
        companyData,
        missionTemplatesCompanyId,
        setMissionTemplatesCompanyId,
        language,
    } = UseContext();
    const [isLoading, setIsLoading] = useState(() => {
        if (companyData?._id == null) return true;
        return missionTemplatesCompanyId !== String(companyData._id);
    });
    const [searchQuery, setSearchQuery] = useState("");
    const hasCompanyLoaded = companyData?._id != null;
    const scopeIdRef = useRef(null);
    scopeIdRef.current = missionTemplatesCompanyId;
    const currentCompanyIdRef = useRef(null);
    currentCompanyIdRef.current =
        companyData?._id != null ? String(companyData._id) : null;

    useEffect(() => {
        if (!companyData?._id) return undefined;
        const requestedId = String(companyData._id);
        const hasCacheForCompany = scopeIdRef.current === requestedId;
        setIsLoading(!hasCacheForCompany);

        let cancelled = false;

        ApiDataBase.getMissionTemplates(
            { idCompany: requestedId },
            (res) => {
                if (cancelled) return;
                if (requestedId !== currentCompanyIdRef.current) return;

                const list = res?.templates || [];
                setMissionTemplates(list);
                setMissionTemplatesCompanyId(requestedId);
                setIsLoading(false);
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
                toast.error("Impossible de charger les fiches de poste");
                setIsLoading(false);
            }
        );

        return () => {
            cancelled = true;
        };
    }, [refreshMissionTemplates, companyData?._id, setMissionTemplates, setMissionTemplatesCompanyId]);

    const visibleTemplates = useMemo(
        () => filterMissionTemplates(missionTemplates, searchQuery),
        [missionTemplates, searchQuery]
    );

    const handleCreate = () => {
        resetTemplateData();
        navigate("/missionTemplates/create");
    };

    const handlePreview = (template) => {
        setPreviewMissionTemplate(template);
        setModalType("missionTemplate_preview");
        setModalShow(true);
    };

    const isListLoading = !hasCompanyLoaded || isLoading;
    const hasTemplates = missionTemplates && missionTemplates.length > 0;

    const renderContent = () => {
        if (isListLoading) {
            return (
                <div
                    className="grid grid-cols-2 gap-3 lg:grid-cols-3"
                    role="status"
                    aria-busy="true"
                >
                    <span className="sr-only">
                        {language.getValue(
                            "missionTemplate.list.loading",
                            "Chargement des fiches de poste…"
                        )}
                    </span>
                    {SKELETON_CARDS.map((key) => (
                        <TemplateCardSkeleton key={key} />
                    ))}
                </div>
            );
        }
        if (!hasTemplates) {
            return (
                <EmptyStateCompany
                    type={"job_post_template"}
                    onAction={handleCreate}
                />
            );
        }
        if (visibleTemplates.length === 0) {
            return (
                <p className="py-12 text-center ft-sm text-gray-500" role="status">
                    {language.getValue(
                        "missionTemplate.list.noResult",
                        "Aucune fiche de poste ne correspond à votre recherche."
                    )}
                </p>
            );
        }
        return (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                {visibleTemplates.map((template) => (
                    <TemplateCard
                        key={template._id}
                        template={template}
                        onPreview={handlePreview}
                    />
                ))}
            </div>
        );
    };

    return (
        <PageForm>
            <SubNavbar>
                <div className="w-full h-full flex items-center justify-between gap-4">
                    <div className="w-[230px] max-w-full">
                        <StyledInput
                            type={"text"}
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            isDisabled={isListLoading || !hasTemplates}
                            placeholder={language.getValue(
                                "missionTemplate.list.searchPlaceholder",
                                "Rechercher"
                            )}
                            imgFront={<SearchIcon wh={16} color="#6B7280" />}
                        />
                    </div>
                    <Button color={"PRIMARY"} size={"SM"} onClick={handleCreate}>
                        <span aria-hidden="true">
                            <Plus wh={12} color={"#FFFFFF"} />
                        </span>
                        {language.getValue("missionTemplate.list.create", "Créer")}
                    </Button>
                </div>
            </SubNavbar>
            <div className="p-4">{renderContent()}</div>
        </PageForm>
    );
};

export default MissionTemplates;
